import { PaymentMethod } from "model/Gig";
import { SalaryType, WorkerEmploymentType } from "model/Salary";
import { isSettingEnabled } from "model/settings.logic";
import {
  EMPLOYMENT_TYPE_CONSULTANT,
  EMPLOYMENT_TYPE_PROJECT_EMPLOYEE,
  EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE,
  EMPLOYMENT_TYPE_TRAINEE,
} from "model/OrganizationalStructure";

export interface EmploymentUiRules {
  isSimflexityEmployee: boolean;
  isTraineeEmployee: boolean;
  isConsultantEmployee: boolean;
  isProjectEmployee: boolean;
  isHourlyTemporaryEmployee: boolean;
  isEndDateRequiredOnSave: boolean;
  canSelectSimflexityPay: boolean;
}

interface CompanySettingValue {
  key: string;
  value?: string | null;
}

/**
 * Employment form rules.
 *
 * Rules 1-6 are enforced continuously by `withNormalizedEmploymentFormByRules`,
 * which both panels re-apply from an effect on every render. A value that
 * violates them cannot be entered or kept.
 * 1. Simflexity, consultant, trainee and hourly temporary workers
 *    (TemporaryWorker / ExclusiveTemporaryWorker) must use hourly salary (Hourly).
 * 2. Simflexity employee must use SimflexityPay as payment type.
 * 3. Consultant and trainee must use employee category = "BlueCollar".
 * 4. SimflexityPay may be selected for other employment types only when:
 *    - the company has a Flex subscription,
 *    - SimflexityPay is enabled in company settings,
 *    - salary type is Hourly.
 * 5. If SimflexityPay is not allowed in the current context, payment type is normalized to "None".
 * 6. End date is required on save for project employees and trainees.
 *
 * Rule 7 is a prefill, not an enforced rule. `withEmploymentTypeChangePrefill`
 * runs once, from the field setter, when the user picks a new employment type;
 * the prefilled values stay editable afterwards. It deliberately does not live in
 * the enforcement effect above — re-applying it every render would pin the fields
 * and make them impossible to edit.
 * 7. Picking employment type "Employee" in the dropdown overwrites the contract
 *    terms with the statutory minimums (30 notice days, 25 vacation days) and the
 *    company address as workplace, replacing whatever was there.
 *
 * Employment templates never reach rule 7: both panels apply a template by writing
 * the whole employment form at once, not through the field setters, so a template's
 * own contract terms always stand.
 */

/**
 * Derives UI rule flags for employment/salary form behavior from rule set above.
 */
export const toEmploymentUiRules = (
  employmentType: string,
  salaryType: string,
  subscription: string | undefined,
  companySettings: CompanySettingValue[],
): EmploymentUiRules => {
  const isSimflexityEmployee =
    employmentType === EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE;
  const isTraineeEmployee = employmentType === EMPLOYMENT_TYPE_TRAINEE;
  const isConsultantEmployee = employmentType === EMPLOYMENT_TYPE_CONSULTANT;
  const isProjectEmployee = employmentType === EMPLOYMENT_TYPE_PROJECT_EMPLOYEE;
  const isHourlyTemporaryEmployee =
    employmentType === "TemporaryWorker" ||
    employmentType === "ExclusiveTemporaryWorker";
  const isEndDateRequiredOnSave = isProjectEmployee || isTraineeEmployee;
  const isFlexCompany = subscription?.includes("Flex") === true;

  const hasSimflexityPay = isSettingEnabled(companySettings, "SimflexityPay");

  const canSelectSimflexityPay = isSimflexityEmployee
    ? true
    : isFlexCompany && hasSimflexityPay && salaryType === SalaryType.Hourly;

  return {
    isSimflexityEmployee,
    isTraineeEmployee,
    isConsultantEmployee,
    isProjectEmployee,
    isHourlyTemporaryEmployee,
    isEndDateRequiredOnSave,
    canSelectSimflexityPay,
  };
};

/**
 * Applies the derived employment rules to current form values and returns a normalized copy.
 *
 * What this function may change:
 * - `salaryType`: forced to `Hourly` for Simflexity, consultant, trainee and
 *   hourly temporary workers.
 * - `employeeCategory`: forced to `"BlueCollar"` for consultants and trainees.
 * - `paymentType`:
 *   - forced to `"SimflexityPay"` for Simflexity employees,
 *   - reset to `"None"` when SimflexityPay is selected but not allowed by rules.
 *
 * What this function does not do:
 * - It does not mutate the input object.
 * - It does not validate required fields or return errors.
 * - It only enforces cross-field consistency based on `rules`.
 */
export const withNormalizedEmploymentFormByRules = (
  employmentDetails: {
    salaryType: string;
    employeeCategory: string;
    paymentType: string;
  },
  rules: EmploymentUiRules,
): {
  salaryType: string;
  employeeCategory: string;
  paymentType: string;
} => {
  let nextSalaryType = employmentDetails.salaryType;
  let nextEmployeeCategory = employmentDetails.employeeCategory;
  let nextPaymentType = employmentDetails.paymentType;

  if (
    (rules.isSimflexityEmployee ||
      rules.isConsultantEmployee ||
      rules.isTraineeEmployee ||
      rules.isHourlyTemporaryEmployee) &&
    nextSalaryType !== SalaryType.Hourly
  ) {
    nextSalaryType = SalaryType.Hourly;
  }

  if (rules.isConsultantEmployee || rules.isTraineeEmployee) {
    nextEmployeeCategory = "BlueCollar";
  }

  if (
    rules.isSimflexityEmployee &&
    nextPaymentType !== PaymentMethod.SIMFLEXITY_PAY
  ) {
    nextPaymentType = PaymentMethod.SIMFLEXITY_PAY;
  }

  if (
    !rules.isSimflexityEmployee &&
    nextPaymentType === PaymentMethod.SIMFLEXITY_PAY &&
    !rules.canSelectSimflexityPay
  ) {
    nextPaymentType = PaymentMethod.NONE;
  }

  return {
    salaryType: nextSalaryType,
    employeeCategory: nextEmployeeCategory,
    paymentType: nextPaymentType,
  };
};

export interface EmploymentContractTerms {
  readonly noticeDays: string;
  readonly vacationDays: string;
  readonly workplace: string;
}

const EMPLOYEE_PREFILL_NOTICE_DAYS = "30";
const EMPLOYEE_PREFILL_VACATION_DAYS = "25";

/**
 * Rule 7: the contract terms to prefill for a newly picked employment type,
 * or `null` when the type has no prefill.
 */
export const toEmploymentTypeChangePrefill = (
  employmentType: string,
  companyAddress: string | null | undefined,
): EmploymentContractTerms | null => {
  if (employmentType !== WorkerEmploymentType.EMPLOYEE) return null;

  return {
    noticeDays: EMPLOYEE_PREFILL_NOTICE_DAYS,
    vacationDays: EMPLOYEE_PREFILL_VACATION_DAYS,
    workplace: companyAddress?.trim() ?? "",
  };
};

/**
 * Applies the rule 7 prefill to an already-updated employment form, so a field
 * setter can pass the field it just wrote and get the form back unchanged unless
 * that field was `employmentType`.
 */
export const withEmploymentTypeChangePrefill = <
  TForm extends EmploymentContractTerms,
>(
  form: TForm,
  changedField: PropertyKey,
  changedValue: unknown,
  companyAddress: string | null | undefined,
): TForm => {
  if (changedField !== "employmentType" || typeof changedValue !== "string") {
    return form;
  }

  const prefill = toEmploymentTypeChangePrefill(changedValue, companyAddress);
  if (prefill === null) return form;

  return { ...form, ...prefill };
};
